import { useState } from "react";
import { getPresignedUrl, uploadFileToS3 } from "../../../Action/s3UploadApi";

const checkImageDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      const aspectRatio = width / height;
      const tolerance = 0.05;

      const instagramRatios = {
        square: { ratio: 1 },
        portrait: { ratio: 4 / 5 },
        landscape: { ratio: 1.91 },
      };

      const isSquare =
        Math.abs(aspectRatio - instagramRatios.square.ratio) <= tolerance;
      const isPortrait =
        Math.abs(aspectRatio - instagramRatios.portrait.ratio) <= tolerance;
      const isLandscape =
        Math.abs(aspectRatio - instagramRatios.landscape.ratio) <= tolerance;

      const fitsInstagram = isSquare || isPortrait || isLandscape;

      resolve({
        width,
        height,
        aspectRatio,
        fitsInstagram,
        detectedFormat: isSquare
          ? "square"
          : isPortrait
          ? "portrait"
          : isLandscape
          ? "landscape"
          : "custom",
      });
    };

    img.onerror = () => {
      resolve({
        width: 0,
        height: 0,
        aspectRatio: 0,
        fitsInstagram: false,
        detectedFormat: "unknown",
      });
    };

    img.src = URL.createObjectURL(file);
  });
};

export const useMediaAndCropping = ({
  contentType,
  platforms,
  setSnackbarMessage,
  setSnackbarSeverity,
  setOpenSnackbar,
}) => {
  const [mediaFile, setMediaFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Instagram cropper states
  const [instagramCropperOpen, setInstagramCropperOpen] = useState(false);
  const [currentCroppingImage, setCurrentCroppingImage] = useState(null);
  const [currentCroppingIndex, setCurrentCroppingIndex] = useState(null);
  const [pendingImages, setPendingImages] = useState([]);

  const handleInstagramCropperClose = () => {
    setInstagramCropperOpen(false);
    setCurrentCroppingImage(null);
    setCurrentCroppingIndex(null);
    setPendingImages([]);

    setSnackbarMessage("Instagram cropping cancelled");
    setSnackbarSeverity("info");
    setOpenSnackbar(true);
  };

  const handleInstagramCropSave = async (croppedImage) => {
    try {
      setIsUploading(true);

      const originalImage = pendingImages[currentCroppingIndex];
      const base = (
        originalImage?.originalName ||
        originalImage?.name ||
        "image"
      ).replace(/\.[^/.]+$/, "");
      const uploadName = `${base}-instagram.jpeg`;

      const presignedData = await getPresignedUrl(
        uploadName,
        croppedImage.type || "image/jpeg"
      );
      if (!presignedData?.success) {
        throw new Error("Failed to get upload URL for cropped image");
      }

      await uploadFileToS3(presignedData.data.uploadAPIUrl, croppedImage);

      Object.defineProperty(croppedImage, "originalName", {
        value: originalImage?.originalName || originalImage?.name || uploadName,
        writable: true,
        enumerable: true,
      });
      Object.defineProperty(croppedImage, "uniqueName", {
        value: presignedData.data.fileName,
        writable: true,
        enumerable: true,
      });
      Object.defineProperty(croppedImage, "s3Url", {
        value: presignedData.data.fileUrl,
        writable: true,
        enumerable: true,
      });
      Object.defineProperty(croppedImage, "s3Key", {
        value: presignedData.data.key,
        writable: true,
        enumerable: true,
      });
      Object.defineProperty(croppedImage, "isInstagramCropped", {
        value: true,
        writable: true,
        enumerable: true,
      });

      const currentFiles = mediaFile || [];
      const newFiles = [
        ...(Array.isArray(currentFiles) ? currentFiles : [currentFiles]),
        croppedImage,
      ];
      setMediaFile(newFiles);

      const nextIndex = currentCroppingIndex + 1;
      if (nextIndex < pendingImages.length) {
        setCurrentCroppingIndex(nextIndex);
        setCurrentCroppingImage(pendingImages[nextIndex]);
      } else {
        setInstagramCropperOpen(false);
        setCurrentCroppingImage(null);
        setCurrentCroppingIndex(null);
        setPendingImages([]);

        setSnackbarMessage("✅ All images processed for Instagram!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.error("Cropped upload error:", err);
      setSnackbarMessage(
        `Failed to upload cropped image. ${err?.message || ""}`.trim()
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validFiles = [];
    const errors = [];
    const maxImageSize = 10 * 1024 * 1024;
    const maxVideoSize = 50 * 1024 * 1024;

    try {
      setIsUploading(true);

      for (let file of files) {
        const maxSize = contentType === "Image" ? maxImageSize : maxVideoSize;
        const isValidType =
          contentType === "Image"
            ? file.type.startsWith("image/")
            : file.type.startsWith("video/");

        if (!isValidType) {
          errors.push(
            `${
              file.name
            }: Please select a valid ${contentType.toLowerCase()} file`
          );
          continue;
        }

        if (file.size > maxSize) {
          const maxSizeMB = maxSize / 1024 / 1024;
          errors.push(
            `${file.name}: File size too large. Maximum ${maxSizeMB}MB allowed`
          );
          continue;
        }

        const currentCount = mediaFile
          ? Array.isArray(mediaFile)
            ? mediaFile.length
            : 1
          : 0;
        const maxCount = contentType === "Image" ? 10 : 5;
        if (currentCount + validFiles.length >= maxCount) {
          errors.push(
            `Maximum ${maxCount} ${contentType.toLowerCase()}s allowed`
          );
          break;
        }

        try {
          const presignedData = await getPresignedUrl(file.name, file.type);
          if (!presignedData.success) {
            errors.push(`Failed for ${file.name}`);
            continue;
          }
          await uploadFileToS3(presignedData.data.uploadAPIUrl, file);

          const fileWithS3Data = new File([file], file.name, {
            type: file.type,
            lastModified: file.lastModified,
          });

          fileWithS3Data.originalName = file.name;
          fileWithS3Data.uniqueName = presignedData.data.fileName;
          fileWithS3Data.s3Url = presignedData.data.fileUrl;
          fileWithS3Data.s3Key = presignedData.data.key;

          if (contentType === "Image" && file.type.startsWith("image/")) {
            const dimensions = await checkImageDimensions(file);
            fileWithS3Data.dimensions = dimensions;
            fileWithS3Data.fitsInstagram = dimensions.fitsInstagram;
            fileWithS3Data.detectedFormat = dimensions.detectedFormat;
          }

          validFiles.push(fileWithS3Data);
        } catch (err) {
          errors.push(`Failed to upload ${file.name} to storage`);
          console.error("S3 upload error:", err);
          continue;
        }
      }

      if (errors.length > 0) {
        setSnackbarMessage(errors.join(". "));
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }

      if (validFiles.length > 0) {
        const isInstagramSelected = platforms.includes("Instagram");
        const hasImages = validFiles.some((f) => f.type.startsWith("image/"));

        if (isInstagramSelected && hasImages && contentType === "Image") {
          const imageFiles = validFiles.filter((f) =>
            f.type.startsWith("image/")
          );
          const imagesNeedingCrop = imageFiles.filter((f) => !f.fitsInstagram);
          const imagesAlreadyFit = imageFiles.filter((f) => f.fitsInstagram);
          const nonImageFiles = validFiles.filter(
            (f) => !f.type.startsWith("image/")
          );

          const filesToAddImmediately = [...imagesAlreadyFit, ...nonImageFiles];
          if (filesToAddImmediately.length > 0) {
            const currentFiles = mediaFile || [];
            const newFiles = [
              ...(Array.isArray(currentFiles) ? currentFiles : [currentFiles]),
              ...filesToAddImmediately,
            ];
            setMediaFile(newFiles);
          }

          if (imagesAlreadyFit.length > 0) {
            const formatCounts = imagesAlreadyFit.reduce((counts, f) => {
              counts[f.detectedFormat] = (counts[f.detectedFormat] || 0) + 1;
              return counts;
            }, {});
            const formatInfo = Object.entries(formatCounts)
              .map(([format, count]) => `${count} ${format}`)
              .join(", ");

            setSnackbarMessage(
              `✅ ${
                imagesAlreadyFit.length
              } image(s) already fit Instagram format (${formatInfo}). ${
                imagesNeedingCrop.length > 0
                  ? `${imagesNeedingCrop.length} image(s) need cropping.`
                  : ""
              }`
            );
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
          }

          if (imagesNeedingCrop.length > 0) {
            setPendingImages(imagesNeedingCrop);
            setCurrentCroppingIndex(0);
            setCurrentCroppingImage(imagesNeedingCrop[0]);
            setInstagramCropperOpen(true);

            if (imagesAlreadyFit.length === 0) {
              setSnackbarMessage(
                `📸 ${imagesNeedingCrop.length} image(s) need Instagram formatting. Please crop each image.`
              );
              setSnackbarSeverity("info");
              setOpenSnackbar(true);
            }
          } else if (imagesAlreadyFit.length === 0) {
            setSnackbarMessage(
              `Uploaded ${validFiles.length} ${contentType.toLowerCase()}${
                validFiles.length > 1 ? "s" : ""
              } successfully!`
            );
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
          }
        } else {
          const currentFiles = mediaFile || [];
          const newFiles = [
            ...(Array.isArray(currentFiles) ? currentFiles : [currentFiles]),
            ...validFiles,
          ];
          setMediaFile(newFiles);

          setSnackbarMessage(
            `Uploaded ${validFiles.length} ${contentType.toLowerCase()}${
              validFiles.length > 1 ? "s" : ""
            } successfully!`
          );
          setSnackbarSeverity("success");
          setOpenSnackbar(true);
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      setSnackbarMessage("Failed to process uploads. Please try again.");
      setSnackbarSeverity(validFiles.length ? "success" : "error");
      setOpenSnackbar(true);
    } finally {
      setIsUploading(false);
      setOpenSnackbar(true);
    }
  };

  const removeMediaFile = (idx) =>
    setMediaFile((mediaFile || []).filter((_, i) => i !== idx));
  const removeAllMediaFiles = () => setMediaFile(null);

  const resetCropping = () => {
    setInstagramCropperOpen(false);
    setCurrentCroppingImage(null);
    setCurrentCroppingIndex(null);
    setPendingImages([]);
  };

  return {
    // state
    mediaFile,
    setMediaFile,
    isUploading,

    // upload handlers
    handleMediaUpload,
    removeMediaFile,
    removeAllMediaFiles,

    // instagram cropper state/handlers
    instagramCropperOpen,
    currentCroppingImage,
    handleInstagramCropperClose,
    handleInstagramCropSave,
    resetCropping,
  };
};

export default useMediaAndCropping;
